import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES, LOGO_URL, WA_PIERCING, WA_TATTOO } from "../../lib/constants";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col overflow-hidden bg-[#0A0A0A]" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <video
          ref={videoRef}
          src="/videos/hero-video.mp4"
          poster={IMAGES.heroBg}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover opacity-35 grayscale-[0.35]"
          data-testid="hero-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/60 to-[#0A0A0A]" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#E60000]/10 blur-[160px]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pt-32 pb-24 text-center">
        <motion.img
          src={LOGO_URL}
          alt="Diggy Shop — Tattoo, Piercing, Joias e Acessórios"
          data-testid="hero-logo"
          style={{ y: logoY }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="w-56 sm:w-72 lg:w-80 h-auto drop-shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
        />

        <p className="mt-10 text-xs sm:text-sm tracking-[0.5em] font-semibold text-white/60" data-testid="hero-tagline">
          TATTOO <span className="text-[#E60000]">•</span> PIERCING <span className="text-[#E60000]">•</span> JOIAS <span className="text-[#E60000]">•</span> ACESSÓRIOS
        </p>

        <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-white" data-testid="hero-headline">
          <MaskedLine delay={0.4}>SEU ESTILO.</MaskedLine>
          <MaskedLine delay={0.55} className="italic text-[#E60000]">SUA IDENTIDADE.</MaskedLine>
        </h1>

        <div className="mt-6 overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="text-sm tracking-[0.3em] text-white/50 font-medium"
            data-testid="hero-since"
          >
            DESDE 1998 <span className="mx-2 text-[#E60000]">—</span> JUNDIAÍ/SP
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            href={WA_TATTOO}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-tattoo-button"
            className="btn-sharp bg-[#E60000] hover:bg-white hover:text-[#0A0A0A] text-white text-sm tracking-[0.15em] font-extrabold px-10 py-5 transition-colors duration-300"
          >
            QUERO FAZER UMA TATTOO
          </a>
          <a
            href={WA_PIERCING}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-piercing-button"
            className="btn-sharp border border-white/40 hover:border-[#E60000] hover:text-[#E60000] text-white text-sm tracking-[0.15em] font-extrabold px-10 py-5 transition-colors duration-300"
          >
            QUERO COLOCAR UM PIERCING
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative z-10 pb-10 flex justify-center"
      >
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ChevronDown className="text-[#E60000]" size={22} />
        </motion.div>
      </motion.div>
    </section>
  );
};
